import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";

const IletisimMesajlari = () => {
  document.title = "İletişim Mesajları | HarmanaGEL";

  const [mesajlar, setMesajlar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [selectedMesaj, setSelectedMesaj] = useState(null);
  const [cevap, setCevap] = useState("");
  const [cevapLoading, setCevapLoading] = useState(false);

  const fetchMesajlar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/ILETISIM_MESAJLARI?$top=500&$orderby=GONDERIM_TARIHI desc`
      );
      const result = await response.json();
      if (result && result.value) setMesajlar(result.value);
    } catch (err) {
      setError("Mesajlar yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMesajlar();
  }, []);

  const handleViewMesaj = (mesaj) => {
    setSelectedMesaj(mesaj);
    setCevap(mesaj.CEVAP || "");
    setModal(true);
    // Okundu olarak işaretle
    if (mesaj.DURUM === "yeni") {
      fetch(`${BASE_URL}/ILETISIM_MESAJLARI(${mesaj.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ DURUM: "okundu" }),
      }).then(() => fetchMesajlar());
    }
  };

  const handleCevapla = async () => {
    if (!cevap.trim()) return;
    setCevapLoading(true);
    try {
      await fetch(`${BASE_URL}/ILETISIM_MESAJLARI(${selectedMesaj.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          CEVAP: cevap,
          CEVAP_TARIHI: new Date().toISOString(),
          DURUM: "cevaplandı",
        }),
      });
      setSuccessMsg("Cevap kaydedildi.");
      setModal(false);
      fetchMesajlar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Cevap kaydedilirken hata oluştu.");
    } finally {
      setCevapLoading(false);
    }
  };

  const getDurumBadge = (durum) => {
    const renkler = { "yeni": "danger", "okundu": "warning", "cevaplandı": "success" };
    const etiketler = { "yeni": "Yeni", "okundu": "Okundu", "cevaplandı": "Cevaplandı" };
    return <Badge color={renkler[durum] || "secondary"}>{etiketler[durum] || durum}</Badge>;
  };

  const yeniCount = useMemo(() => mesajlar.filter(m => m.DURUM === "yeni").length, [mesajlar]);

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Gönderen",
      accessorKey: "AD_SOYAD",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => (
        <div>
          <div className="fw-medium">{cell.getValue()}</div>
          <small className="text-muted">{cell.row.original.EPOSTA}</small>
        </div>
      ),
    },
    {
      header: "Mesaj",
      accessorKey: "MESAJ",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <span
          style={{ maxWidth: 250, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={cell.getValue()}
        >
          {cell.getValue()}
        </span>
      ),
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => getDurumBadge(cell.getValue()),
    },
    {
      header: "Gönderim Tarihi",
      accessorKey: "GONDERIM_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
      },
    },
    {
      header: "Cevap Tarihi",
      accessorKey: "CEVAP_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR") : "-";
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const m = cellProps.row.original;
        return (
          <Button
            color={m.DURUM === "yeni" ? "danger" : "primary"}
            size="sm"
            outline={m.DURUM !== "yeni"}
            onClick={() => handleViewMesaj(m)}
          >
            {m.DURUM === "cevaplandı" ? (
              <><i className="bx bx-show me-1" />Görüntüle</>
            ) : (
              <><i className="bx bx-reply me-1" />Cevapla</>
            )}
          </Button>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="İletişim Mesajları" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {yeniCount > 0 && (
            <Alert color="danger" className="d-flex align-items-center gap-2">
              <i className="bx bx-envelope fs-4" />
              <span><strong>{yeniCount} yeni mesaj</strong> bulunmaktadır.</span>
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">
                      İletişim Mesajları
                      {yeniCount > 0 && <Badge color="danger" pill className="ms-2">{yeniCount}</Badge>}
                    </h4>
                    <Button color="primary" size="sm" onClick={fetchMesajlar} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={mesajlar}
                      isGlobalFilter={true}
                      SearchPlaceholder="Mesaj ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setModal(false)}>
          Mesaj Detayı #{selectedMesaj?.ID}
        </ModalHeader>
        <ModalBody>
          {selectedMesaj && (
            <>
              <div className="mb-3 p-3 bg-light rounded">
                <div className="d-flex justify-content-between mb-2">
                  <div>
                    <strong>{selectedMesaj.AD_SOYAD}</strong>
                    <span className="text-muted ms-2">&lt;{selectedMesaj.EPOSTA}&gt;</span>
                  </div>
                  <small className="text-muted">
                    {selectedMesaj.GONDERIM_TARIHI ? new Date(selectedMesaj.GONDERIM_TARIHI).toLocaleString("tr-TR") : "-"}
                  </small>
                </div>
                <p className="mb-0">{selectedMesaj.MESAJ}</p>
              </div>

              {selectedMesaj.CEVAP && (
                <div className="mb-3 p-3 bg-success bg-opacity-10 rounded border border-success border-opacity-25">
                  <div className="d-flex justify-content-between mb-2">
                    <strong className="text-success">Admin Cevabı</strong>
                    <small className="text-muted">
                      {selectedMesaj.CEVAP_TARIHI ? new Date(selectedMesaj.CEVAP_TARIHI).toLocaleString("tr-TR") : ""}
                    </small>
                  </div>
                  <p className="mb-0">{selectedMesaj.CEVAP}</p>
                </div>
              )}

              <div>
                <label className="form-label fw-medium">
                  {selectedMesaj.CEVAP ? "Cevabı Güncelle" : "Cevap Yaz"}
                </label>
                <Input
                  type="textarea"
                  rows={4}
                  value={cevap}
                  onChange={(e) => setCevap(e.target.value)}
                  placeholder="Cevabınızı buraya yazın..."
                />
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)}>Kapat</Button>
          <Button color="primary" onClick={handleCevapla} disabled={cevapLoading || !cevap.trim()}>
            {cevapLoading ? <Spinner size="sm" /> : <><i className="bx bx-reply me-1" />Cevap Gönder</>}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default IletisimMesajlari;
